import { useMemo } from "react"

import { Obj } from "scrivito"
import {
  ProductAttributes,
  ProductObj,
  ProductObjClass
} from "scrivito/Objs/Product/ProductObjClass"
import { ProductBaseVariantAttributes } from "scrivito/Objs/ProductBaseVariant/ProductBaseVariantObjClass"
import { DEFAULT_FUZZY_SEARCH_OPTIONS } from "shared/constants/search"
import { useFuzzySearchWithRanges } from "shared/lib/useFuzzySearchWithRanges"
import { compareVariantKeys } from "shared/lib/variant-key/compareVariantKeys"

import { getProductType } from "entities/products/lib/getProductType"
import { useProductObj } from "entities/products/model/useProductObj"

import { transformProductObjToProductSearchResult } from "../lib/transformProductObjToProductSearchResult"
import { ProductSearchResult } from "../types/GlobalSearch"

interface UseProductSearchProps {
  searchTerm?: string | null
}

function getProductTitle(product: ProductObj) {
  return product.get("title")
}

export function useProductObjSearch({
  searchTerm = ""
}: UseProductSearchProps) {
  const products = ProductObjClass.where(
    "isHiddenInPortfolio",
    "equals",
    false
  ).toArray()
  const { productObj, productBaseVariantObj } = useProductObj(
    searchTerm ?? "",
    { returnHiddenProducts: false }
  )

  const fuzzySearch = useFuzzySearchWithRanges<ProductObj>({
    getSearchableString: getProductTitle,
    options: DEFAULT_FUZZY_SEARCH_OPTIONS
  })

  const productsKey = products.map(product => product.id()).join(":")
  const fuzzyResults = useMemo(() => {
    return fuzzySearch(products, searchTerm)
  }, [productsKey, searchTerm])

  const options = useMemo<ProductSearchResult[]>(() => {
    if (!searchTerm || searchTerm.length < 2) return []

    const result: ProductSearchResult[] = []
    const productByVariantKey = productBaseVariantObj || productObj

    if (productByVariantKey) {
      const variantKey = (
        productByVariantKey as Obj<
          ProductAttributes | ProductBaseVariantAttributes
        >
      ).get("variantKey")

      result.push({
        ...transformProductObjToProductSearchResult(productByVariantKey),
        matchInfo: {
          variantKey: {
            value: variantKey,
            ranges: compareVariantKeys(variantKey, searchTerm)?.ranges1
          }
        }
      })
    }

    return [
      ...result,
      ...fuzzyResults
        .filter(
          fuzzyResult =>
            fuzzyResult.item.get("productType") !== getProductType(searchTerm)
        )
        .map(fuzzyResult => ({
          ...transformProductObjToProductSearchResult(fuzzyResult.item),
          matchInfo: {
            title: {
              value: fuzzyResult.item.get("title"),
              ranges: fuzzyResult.ranges
            }
          }
        }))
    ]
  }, [productObj?.id(), productBaseVariantObj?.id(), searchTerm, productsKey])

  return { results: options, isLoading: false }
}
